/**
 * Centralized error reporting service
 * Integrates with error tracking services and logs errors to the backend
 */
import { auth } from '../config/firebase';

// Store a limited number of errors in memory to prevent flooding
const errorCache = new Set();
const MAX_CACHE_SIZE = 100;
const ERROR_COOLDOWN_MS = 10000; // 10 seconds between identical errors

// Error severity levels
const ErrorSeverity = {
  INFO: 'info',      // Informational issues that don't affect functionality
  WARNING: 'warning', // Problems that might affect user experience but not critical
  ERROR: 'error',    // Significant issues that affect functionality
  CRITICAL: 'critical' // Severe issues that break core functionality
};

// Group similar errors to reduce noise
const getErrorFingerprint = (error, errorInfo) => {
  // Extract most specific information for grouping
  const message = error?.message || error?.toString() || 'Unknown error';
  const type = errorInfo?.type || 'general';
  const component = errorInfo?.component || 'unknown';
  const endpoint = errorInfo?.endpoint || '';
  
  // Create a fingerprint that identifies similar errors
  return `${type}:${component}:${endpoint}:${message.substring(0, 100)}`;
};

// Main error reporting function
const reportError = (error, errorInfo = {}) => {
  // Attach default severity if not provided
  if (!errorInfo.severity) {
    errorInfo.severity = errorInfo.type === 'network_error' || errorInfo.type === 'api_error' 
      ? ErrorSeverity.ERROR 
      : ErrorSeverity.WARNING;
  }

  // Generate fingerprint for this error
  const fingerprint = getErrorFingerprint(error, errorInfo);

  // Check if we've reported this error recently
  if (errorCache.has(fingerprint)) {
    return; // Skip duplicate errors during cooldown period
  }

  // Add to cache with auto-cleanup
  errorCache.add(fingerprint);
  
  // Clear from cache after cooldown
  setTimeout(() => {
    errorCache.delete(fingerprint);
  }, ERROR_COOLDOWN_MS);

  // If cache is getting too large, clear oldest entries
  if (errorCache.size > MAX_CACHE_SIZE) {
    // Clear half of the cache (older entries will be at the beginning due to insertion order)
    const toRemove = Math.floor(MAX_CACHE_SIZE / 2);
    let count = 0;
    for (const key of errorCache) {
      errorCache.delete(key);
      count++;
      if (count >= toRemove) break;
    }
  }

  // In development, provide detailed logs
  if (process.env.NODE_ENV === 'development') {
    console.group('🚨 Error Report');
    console.error('Error:', error);
    console.info('Details:', errorInfo);
    console.groupEnd();
    return; // Don't send to backend in development
  }
  
  // In production, send to error tracking service
  try {
    // Get current auth state for context
    const currentUser = auth.currentUser;
    
    // Prepare error payload
    const errorPayload = {
      // Error details
      message: error?.message || error?.toString() || 'Unknown error',
      name: error?.name,
      stack: error?.stack,
      
      // Additional context
      ...errorInfo,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      language: navigator.language,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      
      // User context (non-identifying)
      userContext: currentUser ? {
        // Don't include email or other PII
        isAuthenticated: true,
        uid: currentUser.uid,
        lastAuthAt: currentUser.metadata?.lastSignInTime
      } : {
        isAuthenticated: false
      }
    };
    
    // Send to backend logging endpoint
    fetch(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://localhost:5000' : 'http://localhost:5000')}/logs/error`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        // Add auth header if user is logged in
        ...currentUser ? {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        } : {}
      },
      // Only send error if we're in production
      body: JSON.stringify(errorPayload),
    }).catch(e => {
      // Silent fail - don't want errors in error reporting
      console.debug('Error reporting failed to send to backend');
    });
    
    // If you're using a service like Sentry, you'd add it here:
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { extra: errorInfo });
    // }
    
  } catch (e) {
    // Silent fail in production
    if (process.env.NODE_ENV === 'development') {
      console.error('Error reporting failed:', e);
    }
  }
};

// Track unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  reportError(event.reason, { 
    type: 'unhandledrejection', 
    severity: ErrorSeverity.ERROR
  });
});

// Track uncaught exceptions
window.addEventListener('error', (event) => {
  // Don't report 404s for assets/resources
  if (event.target && (event.target.tagName === 'SCRIPT' ||
                      event.target.tagName === 'LINK' ||
                      event.target.tagName === 'IMG')) {
    reportError(new Error(`Resource error: ${event.target.src || event.target.href}`), { 
      type: 'resource_error',
      severity: ErrorSeverity.WARNING,
      resource: event.target.src || event.target.href
    });
  } else {
    reportError(event.error || new Error(event.message), { 
      type: 'uncaught',
      severity: ErrorSeverity.ERROR,
      lineNumber: event.lineno,
      columnNumber: event.colno
    });
  }
  // Don't prevent default browser error handling in development
  return process.env.NODE_ENV === 'production';
});

// Optional: Track current page for context in error reports
let currentPage = window.location.pathname;
window.addEventListener('popstate', () => {
  currentPage = window.location.pathname;
  // Could log navigation events if desired
  if (process.env.NODE_ENV === 'development') {
    console.debug(`Navigation to: ${currentPage}`);
  }
});

// Add current page to error context
const withPageContext = (context = {}) => {
  return { ...context, currentPage };
};

// Expert helper for logging different severity levels
const logger = {
  info: (message, context = {}) => {
    reportError(new Error(message), withPageContext({ ...context, severity: ErrorSeverity.INFO }));
  },
  warn: (message, context = {}) => {
    reportError(new Error(message), withPageContext({ ...context, severity: ErrorSeverity.WARNING }));
  },
  error: (error, context = {}) => {
    reportError(error, withPageContext({ ...context, severity: ErrorSeverity.ERROR }));
  },
  critical: (error, context = {}) => {
    reportError(error, withPageContext({ ...context, severity: ErrorSeverity.CRITICAL }));
  }
};

export { reportError, logger, ErrorSeverity };

/**
 * Usage:
 * 
 * // Basic error reporting
 * import { reportError } from './services/errorReporting';
 * 
 * try {
 *   // code that might throw
 * } catch (error) {
 *   reportError(error, { 
 *     component: 'MyComponent',
 *     operation: 'loadData'
 *   });
 * }
 * 
 * // Convenience loggers
 * import { logger } from './services/errorReporting';
 * 
 * // Informational logging
 * logger.info('User completed onboarding', { userId: '123' });
 * 
 * // Warning logging
 * logger.warn('API response slow', { endpoint: '/api/data', responseTime: 3500 });
 * 
 * // Error logging
 * try {
 *   // code that might throw
 * } catch (error) {
 *   logger.error(error, { component: 'PaymentProcessor' });
 * }
 * 
 * // Critical error logging
 * try {
 *   // Critical operation
 * } catch (error) {
 *   logger.critical(error, { feature: 'checkout', orderId: '1234' });
 * }
 */
